import { useRef } from 'react';
import { TransitionType } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.types';
import { ImageElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { TRANSITION_MINIMUM_DURATION } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.constants';
import { TransitionDurationSlider } from './TransitionDurationSlider';
import { TransitionTypeDropdown } from './TransitionTypeDropdown';
import { TransitionRotation } from './TransitionRotation';
import { TransitionMove } from './TransitionMove';
import { TransitionLifeCycle } from './TransitionLifeCycle';
import { useTransitionPopup } from './useTransitionPopup';

export const TransitionPopup = ({ selectedElement }: { selectedElement: ImageElement | VideoElement }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    parentDuration,
    isElementDurationTooSmall,
    transitionLifeCycle,
    maxDuration,
    currentTransition,
    onClickTransitionLifeCycleTab,
    updateTransitionType,
    updateTransitionRotateDirection,
    updateTransitionMoveDirection,
    updateTransitionDuration
  } = useTransitionPopup({ selectedElement });

  if (!selectedElement) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="absolute left-1/2 top-full mb-1 mt-2 flex w-[250px] -translate-x-1/2 flex-col rounded-lg border bg-white p-2.5 shadow-lg"
    >
      {isElementDurationTooSmall ? (
        <div className="mb-2 rounded-md border border-black border-opacity-10 bg-orange-50 p-2.5 text-2xs font-medium">
          Transition can’t be applied — media duration ({parentDuration.toFixed(1)} ms) is below{' '}
          {TRANSITION_MINIMUM_DURATION} ms.
        </div>
      ) : null}
      <TransitionLifeCycle
        selectedElement={selectedElement}
        transitionLifeCycle={transitionLifeCycle}
        onClickTransitionLifeCycleTab={onClickTransitionLifeCycleTab}
      />
      <TransitionTypeDropdown
        disabled={isElementDurationTooSmall}
        selected={currentTransition ? currentTransition.type : null}
        onChange={updateTransitionType}
      />
      {currentTransition && currentTransition.type === TransitionType.ROTATE ? (
        <TransitionRotation
          selectedValue={currentTransition.properties.rotateDirection}
          onChange={updateTransitionRotateDirection}
          disabled={isElementDurationTooSmall}
        />
      ) : null}
      {currentTransition && currentTransition.type === TransitionType.MOVE ? (
        <TransitionMove
          selectedValue={currentTransition.properties.moveDirection}
          onChange={updateTransitionMoveDirection}
          disabled={isElementDurationTooSmall}
        />
      ) : null}
      <TransitionDurationSlider
        key={transitionLifeCycle}
        value={!currentTransition || isElementDurationTooSmall ? 0 : (currentTransition.properties.duration ?? 0)}
        onChange={updateTransitionDuration}
        disabled={isElementDurationTooSmall || !currentTransition}
        maxDuration={maxDuration}
      />
    </div>
  );
};
